import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import ForbiddenCircle from '@betfan/icons/linear/EssentionalUI/ForbiddenCircle.svg?react';
import { LockIcon } from 'assets/icons/LockIcon';
import { formatCouponPrice } from 'features/coupon/utility';
import { BetType } from '../CouponPanel/interface';
import DotIndicator from '../CouponRadioButton/DotIndicator';
import './styles.scss';

interface BetslipButtonProps {
	name: string;
	onClick?: () => void;
	amount?: number | string;
	icon?: React.ReactNode;
	active?: boolean;
	disabled?: boolean;
	isDot?: boolean;
	type?: string;
	depositBalanceBelowTwo?: boolean;
	dotNumber?: number;
	isFreeBetSelected?: boolean;
}

const BetslipButton = ({
	name,
	amount,
	onClick,
	icon,
	active,
	disabled = false,
	depositBalanceBelowTwo,
	type,
	dotNumber,
	isFreeBetSelected,
}: BetslipButtonProps) => {
	const { t } = useTranslation();

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const isThousandNumber = amount && amount.toString().split('.')[0]!.length >= 4;
	const isDecimal = amount?.toString().includes('.');
	const isLargeAmount = isThousandNumber && isDecimal;
	return (
		<button
			className={BetslipButtonStyles.button(active, depositBalanceBelowTwo, type, isFreeBetSelected)}
			onClick={onClick}
			disabled={disabled}
			data-cy="betslip-button"
		>
			{dotNumber != undefined && dotNumber > 0 && <DotIndicator dotNumber={dotNumber} className="absolute -top-3 right-[-6px]" />}
			<div className={'px-1 py-2 flex flex-col'}>
				<div className="text-overline-sm leading-tight tracking-widest font-medium uppercase h-5 min-h-5 flex items-center justify-center">
					{isFreeBetSelected && <LockIcon className="dark:stroke-gray-100 stroke-primary-500" />}
					<div className="flex items-center mr-1">
						{depositBalanceBelowTwo && type === BetType.Deposit && (
							<ForbiddenCircle className="text-error-600 dark:text-error-400 w-3 h-3" />
						)}
					</div>
					<span className="flex items-center">{t(name)}</span>
				</div>
				{amount || amount === 0 ? (
					<div
						className={`relative ${
							isLargeAmount ? 'text-[14px] pb-[2px]' : 'text-[18px]'
						} leading-normal font-normal whitespace-nowrap overflow-hidden justify-center min-h-5 h-5 flex items-end text-ellipsis`}
					>
						<span className="leading-none">
							{type ? formatCouponPrice(Number(amount)) + ' ' + t('currency') : amount.toString().replace(',00', '')}
						</span>
					</div>
				) : (
					icon
				)}
			</div>
		</button>
	);
};

const BetslipButtonStyles = {
	button: (
		active: boolean | undefined,
		depositBalanceBelowTwo: boolean | undefined,
		type: string | undefined,
		isFreeBetSelected: boolean | undefined,
	) => {
		let colors;

		if (active) {
			colors = 'bg-primary-400';
		} else {
			if (depositBalanceBelowTwo && type === BetType.Deposit) {
				colors = 'bg-error-200 dark:bg-error-700 text-error-600   dark:text-error-400';
			} else {
				colors = 'bg-gray-150  dark:bg-dark-400  text-primary-500 dark:text-gray-100';
			}
		}

		return cn(
			'border-none outline-none items-center min-h-[56px] h-[56px] rounded-4 flex-1 relative cursor-pointer min-w-0',
			colors,
			isFreeBetSelected && 'bg-transparent dark:bg-transparent',
		);
	},
};

export default BetslipButton;
